import { env } from '@/lib/env';
import { rpc } from '@/lib/api';
import { tenantRegistration } from './registerSW';

export type PushState =
  | { kind: 'unconfigured' }
  | { kind: 'unsupported'; reason: 'ios-not-installed' | 'browser' | 'no-worker' }
  | { kind: 'denied' }
  | { kind: 'subscribed' }
  | { kind: 'available' };

export function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** Real capability check — never promises push where the platform can't deliver it. */
export async function pushState(slug: string): Promise<PushState> {
  if (!env.vapidPublicKey) return { kind: 'unconfigured' };
  const hasApi = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (!hasApi) return { kind: 'unsupported', reason: isIos() && !isStandalone() ? 'ios-not-installed' : 'browser' };
  if (Notification.permission === 'denied') return { kind: 'denied' };
  const reg = await tenantRegistration(slug);
  if (!reg) return { kind: 'unsupported', reason: 'no-worker' };
  const sub = await reg.pushManager.getSubscription();
  return sub ? { kind: 'subscribed' } : { kind: 'available' };
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function subscription(slug: string): Promise<PushSubscriptionJSON> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('permission_denied');
  const reg = await tenantRegistration(slug);
  if (!reg) throw new Error('no_worker');
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(env.vapidPublicKey!) }));
  return sub.toJSON();
}

export async function subscribeCustomer(slug: string, token: string): Promise<void> {
  const s = await subscription(slug);
  await rpc('subscribe_booking_push', { p_slug: slug, p_token: token, p_endpoint: s.endpoint, p_p256dh: s.keys?.p256dh, p_auth: s.keys?.auth, p_user_agent: navigator.userAgent });
}

export async function subscribeOwner(slug: string): Promise<void> {
  const s = await subscription(slug);
  await (await import('@/lib/supabase')).rpc('owner_save_push_subscription', { p_slug: slug, p_endpoint: s.endpoint, p_p256dh: s.keys?.p256dh, p_auth: s.keys?.auth, p_user_agent: navigator.userAgent });
}

export async function unsubscribeOwner(slug: string): Promise<void> {
  const reg = await tenantRegistration(slug);
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await (await import('@/lib/supabase')).rpc('owner_delete_push_subscription', { p_slug: slug, p_endpoint: sub.endpoint }).catch(() => undefined);
    await sub.unsubscribe();
  }
}
